/**
 * taskShared.js
 *
 * Constants, formatters and the small presentational pieces used by both the
 * board (Dashboard.js) and the single-task page (TaskDetail.js).
 *
 * The presentational components live at module scope on purpose. A component
 * declared inside another component is a new type on every render, which
 * unmounts and remounts its subtree - and a focused input inside it loses
 * focus after a single keystroke.
 */

import { Box, Typography, alpha } from "@mui/material";

export const STAFF_IMAGE_BASE =
  "https://mindsinaction.com.na/assets/media/pictures/";

export const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

// Folders are where a task is FILED. status is its lifecycle, driven by the
// rollup cron from subtasks. They are deliberately independent: a task can
// sit in "Design review" and be inprogress. Both are true.
export const STAGES = [
  { value: "active", label: "Active" },
  { value: "inprogress", label: "In progress" },
  { value: "completed", label: "Completed" },
];

export const PERMISSION_LABEL = {
  view: "View only",
  edit: "View & edit",
  full: "Full rights",
};

export const EMPTY_PROGRESS = {
  subtask_id: "0",
  log_date: "",
  hours_spent: "1",
  progress_percent: "",
  work_done: "",
  blockers: "",
};

/* ─── Helpers ──────────────────────────────────────────────────────────── */

export const today = () => new Date().toISOString().split("T")[0];

export const num = (v) => parseFloat(v) || 0;

export const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const daysBetween = (from, to) => {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const am = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bm = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((bm - am) / 86400000);
};

export const isOverdue = (t) =>
  t?.status !== "completed" && t?.deadline && daysBetween(today(), t.deadline) < 0;

export const initials = (first, last) =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase() || "?";

export const atLeast = (have, need) => {
  const rank = { view: 1, edit: 2, full: 3 };
  return (rank[have] || 0) >= (rank[need] || 0);
};

export const stageLabel = (value) =>
  STAGES.find((s) => s.value === value)?.label || value || "—";

/* ─── Theme tokens ─────────────────────────────────────────────────────── */

/**
 * The one place the page palette is decided, so the board and the detail
 * page cannot drift apart. `colors` is the output of tokens(mode).
 */
export const buildUi = (colors, isDark) => {
  const accent = colors.teal[500];
  const accent2 = colors.newTeal[500];
  return {
    accent,
    accent2,
    pageBg: isDark ? colors.dark[800] : "#e9ebef",
    surface: isDark ? colors.dark[700] : colors.white[900],
    surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
    border: isDark
      ? alpha(colors.white[500], 0.09)
      : alpha(colors.dark[500], 0.1),
    textPrimary: colors.primary[100],
    textSecondary: isDark ? colors.gray[400] : colors.gray[300],
    gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
    shadow: isDark
      ? "0 14px 34px -22px rgba(0,0,0,0.8)"
      : "0 14px 34px -24px rgba(16,24,74,0.35)",
  };
};

export const priorityColor = (colors, p) =>
  ({
    low: colors.gray[500],
    normal: colors.newTeal[500],
    high: colors.orange[500],
    urgent: colors.red[500],
  }[p] || colors.gray[500]);

/* ─── Presentational components ────────────────────────────────────────── */

export const SectionCard = ({ ui, title, hint, action, children }) => (
  <Box
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: "14px",
      boxShadow: ui.shadow,
      p: { xs: 2, md: 3 },
      mb: 3,
    }}
  >
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={2}
      mb={hint ? 0.5 : 2}
    >
      <Typography variant="h6" fontWeight={700} color={ui.textPrimary}>
        {title}
      </Typography>
      {action}
    </Box>
    {hint && (
      <Typography variant="body2" color={ui.textSecondary} mb={2}>
        {hint}
      </Typography>
    )}
    {children}
  </Box>
);

export const DetailRow = ({ ui, label, value, color }) => (
  <Box
    display="flex"
    flexWrap="wrap"
    gap={1}
    py={1.2}
    borderBottom={`1px solid ${alpha(ui.border, 0.6)}`}
  >
    <Typography
      variant="body2"
      color={ui.textSecondary}
      sx={{ minWidth: 150, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: color || ui.textPrimary, flex: 1, whiteSpace: "pre-wrap" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

export const StatCard = ({ ui, icon, label, value, sub, tone }) => (
  <Box
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: "14px",
      p: 2,
      height: "100%",
      boxShadow: ui.shadow,
    }}
  >
    <Box display="flex" alignItems="center" gap={1.2} mb={1}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          color: tone || ui.accent,
          bgcolor: alpha(tone || ui.accent, 0.14),
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" color={ui.textSecondary} fontWeight={600}>
        {label}
      </Typography>
    </Box>
    <Typography variant="h4" fontWeight={700} color={ui.textPrimary}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color={ui.textSecondary}>
        {sub}
      </Typography>
    )}
  </Box>
);
